import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Sparkles, Check, Loader2, ArrowRight, Lock, Clock, MessageSquare, Mic, Camera, Hand } from "lucide-react";
import { toast } from "sonner";
import { getSubmission, createCheckout } from "../lib/api";

export default function Summary() {
    const { submissionId } = useParams();
    const navigate = useNavigate();
    const [submission, setSubmission] = useState(null);
    const [building, setBuilding] = useState(true);
    const [loadingPkg, setLoadingPkg] = useState(null);
    const preferredPkg = (() => {
        try { return sessionStorage.getItem("preferred_package") || ""; } catch (e) { return ""; }
    })();

    useEffect(() => {
        let mounted = true;
        getSubmission(submissionId)
            .then(s => { if (mounted) setSubmission(s); })
            .catch(() => navigate("/quiz"));
        const t = setTimeout(() => mounted && setBuilding(false), 1600);
        return () => { mounted = false; clearTimeout(t); };
    }, [submissionId, navigate]);

    const checkout = async (pkg) => {
        setLoadingPkg(pkg);
        try {
            const res = await createCheckout({
                submission_id: submissionId,
                package_id: pkg,
                origin_url: window.location.origin,
            });
            window.location.href = res.url;
        } catch (e) {
            toast.error("Could not start checkout. Please try again.");
            setLoadingPkg(null);
        }
    };

    if (!submission) {
        return <div className="min-h-screen bg-bone flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-saffron-dark" />
        </div>;
    }

    if (building) {
        return (
            <div className="min-h-screen bg-bone flex items-center justify-center px-5" data-testid="summary-building">
                <div className="text-center max-w-md animate-fade-up">
                    <div className="w-16 h-16 rounded-full bg-saffron border-2 border-ink flex items-center justify-center mx-auto mb-7" style={{ boxShadow: '4px 4px 0 #1A1A1A' }}>
                        <Sparkles className="w-7 h-7 text-ink animate-pulse" />
                    </div>
                    <h1 className="font-heading font-light text-3xl sm:text-4xl">Building your <em className="text-saffron-dark font-medium">preview…</em></h1>
                    <p className="mt-3 text-muted2">Mixing your preferences, lifestyle, and the spice of your kitchen.</p>
                </div>
            </div>
        );
    }

    const firstName = submission.full_name.split(" ")[0];
    const assistantLabel = submission.assistant_name?.trim() || `${firstName}'s Cooking Assistant`;
    const snapshot = buildSnapshot(submission);
    const tonight = buildTonight(submission);

    return (
        <div className="min-h-screen bg-bone pb-24" data-testid="summary-page">
            <div className="bg-ink text-bone">
                <div className="max-w-3xl mx-auto px-5 sm:px-8 py-3.5 flex items-center gap-2">
                    <span className="text-saffron text-lg leading-none">✺</span>
                    <span className="font-heading text-lg font-semibold">My Cooking <em className="text-saffron not-italic">Assistant</em></span>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-12">

                {/* SECTION 1 — HEADLINE */}
                <span className="tag tag-saffron mb-4">Hi {firstName} 👋</span>
                <h1 className="font-heading font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-5" data-testid="hero-headline">
                    Your personalized cooking assistant is ready <span className="not-italic">🍲</span>
                </h1>
                <p className="mt-5 text-lg text-muted2 leading-relaxed max-w-xl">
                    Built for your family, your time, and your kitchen.
                    <br />No more <em>"what should I cook today?"</em> stress.
                </p>

                {/* TOP PRICING — quick action for users who are already sold */}
                <div className="mt-8" data-testid="checkout-top">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-muted2 mb-3 font-semibold">Choose your plan</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <CheckoutButton currency="USD" price="$4.99" onClick={() => checkout("usd")} loading={loadingPkg === "usd"} testId="checkout-usd-top" tone="saffron" />
                        <CheckoutButton currency="INR" price="₹399" onClick={() => checkout("inr")} loading={loadingPkg === "inr"} testId="checkout-inr-top" tone="teal" />
                    </div>
                    <p className="mt-3 text-sm text-muted2">
                        One-time payment <span className="divider-bullet">·</span> Delivered within 24 hours
                        <span className="divider-bullet">·</span>
                        <a href="#why" className="text-saffron-dark underline-offset-2 hover:underline">Or read why it works ↓</a>
                    </p>
                </div>

                {/* SECTION 2 — PERSONALIZATION SNAPSHOT */}
                <div id="why" className="mt-12 bg-cream rounded-2xl border-2 border-ink p-5 sm:p-6 scroll-mt-6" data-testid="snapshot">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-muted2 mb-2 font-semibold">Made just for you</p>
                    <p className="font-heading text-xl sm:text-2xl leading-snug text-ink">
                        {snapshot}
                    </p>
                </div>

                {/* SECTION 3 — REAL OUTPUT */}
                <div className="mt-6 bg-white rounded-2xl border-2 border-ink p-6 sm:p-8" style={{ boxShadow: '6px 6px 0 #1A1A1A' }} data-testid="real-output">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-muted2 mb-3 font-semibold">Here's what it could suggest for you tonight</p>
                    <p className="font-heading text-2xl sm:text-3xl leading-snug">
                        Tonight ({tonight.time}): <em className="text-saffron-dark font-medium">{tonight.dish}</em>
                    </p>
                    <p className="mt-3 text-muted2 leading-relaxed">{tonight.tags.join(" · ")}</p>
                </div>

                {/* SECTION 4 — BEFORE vs AFTER */}
                <div className="mt-12">
                    <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">From overwhelm to <em className="text-saffron-dark font-medium">"make this tonight"</em></h2>
                    <div className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="before-after">
                        <BeforeAfterCard tone="muted" title="Without">
                            <li>Scroll endlessly</li>
                            <li>Guess what works</li>
                            <li>Adjust for everyone</li>
                            <li>Still unsure</li>
                        </BeforeAfterCard>
                        <BeforeAfterCard tone="saffron" title="With">
                            <li>"Make this tonight"</li>
                            <li>Fits your time</li>
                            <li>Uses your ingredients</li>
                            <li>Works for your family</li>
                        </BeforeAfterCard>
                    </div>
                    <p className="mt-5 text-base text-muted2 italic">
                        Most tools give options. This gives a clear answer.
                    </p>
                </div>

                {/* SECTION 5 — WHAT YOU GET */}
                <div className="mt-12">
                    <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">What you'll get within <em className="text-saffron-dark font-medium">24 hours</em></h2>
                    <div className="mt-5 space-y-3" data-testid="what-you-get">
                        <Bullet>Your own private cooking assistant</Bullet>
                        <Bullet>Daily meal decisions — not endless suggestions</Bullet>
                        <Bullet>Instant "what can I cook right now?" help</Bullet>
                        <Bullet>Simple step-by-step guidance when you need it</Bullet>
                        <Bullet>Talk to it in <strong>English</strong>, <strong>ગુજરાતી</strong>, or <strong>हिन्दी</strong> — switch anytime</Bullet>
                    </div>
                </div>

                {/* SECTION 6 — FLEXIBLE USAGE */}
                <div className="mt-12 bg-white rounded-2xl border-2 border-ink p-6 sm:p-8">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight">Use it your way — <em className="text-saffron-dark font-medium">whatever's easiest</em></h2>
                    <p className="mt-2 text-muted2">No typing required if you don't want to.</p>
                    <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="usage-modes">
                        <Mode icon={<Hand className="w-4 h-4" />} label="Tap suggestions" />
                        <Mode icon={<MessageSquare className="w-4 h-4" />} label="Type a craving" />
                        <Mode icon={<Mic className="w-4 h-4" />} label="Speak while cooking" />
                        <Mode icon={<Camera className="w-4 h-4" />} label="Snap your fridge" />
                    </div>
                </div>

                {/* SECTION 7 — SOCIAL PROOF */}
                <div className="mt-12 bg-cream rounded-2xl border-2 border-ink p-6 sm:p-8" data-testid="social-proof">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-muted2 mb-3 font-semibold">From a busy mom</p>
                    <p className="font-heading text-xl sm:text-2xl leading-snug">
                        "Before, I didn't know what to cook every night.
                        <br />Now I just open it and follow. Done."
                    </p>
                    <p className="mt-3 text-sm text-muted2">— Reena · cooks for 4 · 30 mins daily</p>
                </div>

                {/* SECTION 8 — RISK REDUCER */}
                <p className="mt-8 text-center text-base text-muted2">
                    If it doesn't help you, we'll make it right.
                </p>

                {/* SECTION 9 — PAYMENT CTA */}
                <div className="mt-10" data-testid="checkout-section">
                    <p className="text-center text-sm text-muted2 mb-4">
                        Start using it as early as <strong className="text-ink">tomorrow</strong>.
                    </p>
                    <h2 className="text-center font-heading text-3xl sm:text-4xl tracking-tight">
                        Unlock <em className="text-saffron-dark font-medium">{assistantLabel}</em>
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4 mt-6">
                        <CheckoutButton currency="USD" price="$4.99" onClick={() => checkout("usd")} loading={loadingPkg === "usd"} testId="checkout-usd" tone="saffron" highlight={preferredPkg === "usd"} />
                        <CheckoutButton currency="INR" price="₹399" onClick={() => checkout("inr")} loading={loadingPkg === "inr"} testId="checkout-inr" tone="teal" highlight={preferredPkg === "inr"} />
                    </div>
                    <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted2">
                        <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Secure checkout</span>
                        <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Takes 30 seconds</span>
                        <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> One-time payment</span>
                        <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> No subscription</span>
                    </div>

                    {/* FINAL LINE */}
                    <p className="mt-12 text-center font-heading text-xl sm:text-2xl text-ink/80 italic">
                        This isn't a recipe app.
                        <br />It's a decision made for you.
                    </p>
                </div>
            </div>
        </div>
    );
}

function Bullet({ children }) {
    return (
        <div className="flex items-start gap-3">
            <span className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-saffron border-2 border-ink flex items-center justify-center">
                <Check className="w-3 h-3 text-ink" />
            </span>
            <span className="text-base sm:text-lg text-ink leading-snug">{children}</span>
        </div>
    );
}

function Mode({ icon, label }) {
    return (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-ink/15 bg-bone">
            <span className="w-7 h-7 rounded-full bg-saffron border-2 border-ink flex items-center justify-center text-ink">
                {icon}
            </span>
            <span className="text-sm text-ink">{label}</span>
        </div>
    );
}

function BeforeAfterCard({ tone, title, children }) {
    const bg = tone === "saffron" ? "bg-saffron" : "bg-white";
    return (
        <div className={`${bg} rounded-2xl border-2 border-ink p-5 sm:p-6`} style={{ boxShadow: '5px 5px 0 #1A1A1A' }}>
            <p className="text-[11px] uppercase tracking-[0.22em] text-ink/70 mb-3 font-semibold">{title}</p>
            <ul className="space-y-2 text-base sm:text-lg text-ink leading-snug list-disc list-inside marker:text-ink/50">
                {children}
            </ul>
        </div>
    );
}

function CheckoutButton({ currency, price, onClick, loading, testId, tone, highlight }) {
    const bg = tone === "saffron" ? "bg-saffron" : "bg-teal text-bone";
    return (
        <button onClick={onClick} disabled={loading} className={`${bg} ${highlight ? 'cta-pulse' : ''} border-2 border-ink rounded-2xl p-5 text-left transition-all disabled:opacity-50`} style={{ boxShadow: '5px 5px 0 #1A1A1A' }} data-testid={testId} onMouseEnter={(e) => e.currentTarget.style.transform = 'translate(-1px,-1px)'} onMouseLeave={(e) => e.currentTarget.style.transform = ''}>
            <div className="flex items-center justify-between mb-2">
                <p className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${tone === 'saffron' ? 'text-ink/70' : 'text-bone/70'}`}>Pay in {currency}</p>
                {highlight && <span className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-full ${tone === 'saffron' ? 'bg-ink text-saffron' : 'bg-saffron text-ink'}`}>Your pick</span>}
            </div>
            <p className="font-heading text-5xl">{price}</p>
            <p className="mt-3 font-medium flex items-center gap-1.5 text-sm">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Pay {price} & unlock my assistant <ArrowRight className="w-4 h-4" /></>}
            </p>
        </button>
    );
}

/* ---------------- Personalization helpers ---------------- */

function asArray(v) {
    if (Array.isArray(v)) return v;
    if (typeof v === "string" && v.trim()) return [v];
    return [];
}

function buildSnapshot(s) {
    const parts = [];
    const time = s.cooking_time;
    if (time) parts.push(`Made for your ${time.toLowerCase()} cooking window`);

    const household = asArray(s.household);
    if (household.includes("Family (with kids)")) parts.push("family-friendly");
    else if (household.includes("Me + partner")) parts.push("for you & your partner");
    else if (household.includes("Just me")) parts.push("just-for-you portions");
    else if (household.length) parts.push("flexible portions");

    const diet = (s.meal_preference || "").toLowerCase();
    if (diet.includes("jain")) parts.push("Jain-safe");
    else if (diet.includes("vegan")) parts.push("fully plant-based");
    else if (diet.includes("vegetarian")) parts.push("vegetarian-only");

    const prefs = asArray(s.preferences);
    if (prefs.includes("No onion / no garlic")) parts.push("no onion / no garlic");
    if (prefs.includes("Sattvic (light, pure meals)")) parts.push("sattvic & light");
    if (prefs.includes("Kid-friendly") && !parts.includes("family-friendly")) parts.push("kid-friendly");
    if (prefs.includes("High-protein")) parts.push("high-protein");
    if (prefs.includes("Comfort / homestyle food")) parts.push("homestyle comfort");
    if (prefs.includes("Traditional / festival meals")) parts.push("traditional touches");

    parts.push("no overthinking");
    return capitalize(parts.slice(0, 6).join(" · "));
}

function capitalize(str) {
    return str ? str[0].toUpperCase() + str.slice(1) : str;
}

function buildTonight(s) {
    const time = parseTime(s.cooking_time);
    const diet = (s.meal_preference || "").toLowerCase();
    const prefs = asArray(s.preferences);
    const ingredients = asArray(s.ingredients);
    const household = asArray(s.household);

    const isJain = diet.includes("jain") || prefs.includes("No onion / no garlic");
    const isVegan = diet.includes("vegan");
    const isVeg = diet.includes("vegetarian") || isJain || isVegan || diet.includes("no preference") === false;

    const hasPaneer = ingredients.some(i => i.toLowerCase().includes("paneer"));
    const hasFreshVeg = ingredients.includes("Fresh vegetables");
    const hasStaples = ingredients.some(i => i.toLowerCase().includes("staples"));

    const kidFriendly = household.includes("Family (with kids)") || prefs.includes("Kid-friendly");
    const wantsHighProtein = prefs.includes("High-protein");
    const sattvic = prefs.includes("Sattvic (light, pure meals)");

    let dish, tags = [];

    if (time <= 15 && (isVeg || isJain)) {
        dish = sattvic
            ? "moong dal khichdi with a spoon of ghee"
            : (kidFriendly ? "veggie poha with peanuts & lemon" : "masala oats with sautéed veggies");
    } else if (time <= 15) {
        dish = "5-min curd rice with tempered curry leaves";
    } else if (time <= 30) {
        if (isJain) dish = "Jain paneer bhurji + soft phulkas";
        else if (hasPaneer) dish = "paneer bhurji + rotis";
        else if (wantsHighProtein && isVeg) dish = "mixed sprouts chaat + tawa rotis";
        else if (kidFriendly) dish = "tomato-cheese pulao with raita";
        else dish = "tawa veg sabzi + jeera rice";
    } else if (time <= 45) {
        dish = isJain
            ? "Jain dal-tadka, bhindi sabzi, phulkas, jeera rice"
            : "dal tadka, aloo-gobi, phulkas, steamed rice";
    } else {
        dish = "rajma chawal with kachumber salad";
    }

    if (hasStaples) tags.push("Uses what you have");
    else if (hasFreshVeg) tags.push("Built around fresh veg");
    if (kidFriendly) tags.push("Kid-friendly");
    if (isJain) tags.push("Jain-friendly");
    else if (isVegan) tags.push("Vegan");
    else if (isVeg) tags.push("Vegetarian");
    if (sattvic) tags.push("Sattvic");
    if (wantsHighProtein) tags.push("High-protein");
    if (tags.length === 0) tags.push("Made for your kitchen");

    return { time: timeLabel(time), dish, tags: tags.slice(0, 3) };
}

function parseTime(t) {
    if (!t) return 30;
    const s = t.toLowerCase();
    if (s.includes("15")) return 15;
    if (s.includes("30")) return 30;
    if (s.includes("45")) return 45;
    if (s.includes("1 hour") || s.includes("60")) return 60;
    return 30;
}

function timeLabel(mins) {
    if (mins <= 15) return "15 mins";
    if (mins <= 30) return "30 mins";
    if (mins <= 45) return "45 mins";
    return "1 hour";
}
